// After a step is ticked or undone, the page glides to the step that is now open (Yehuda, Oct 27).
// Ticking folds the step and opens the next one, so the page gets shorter; near the bottom the
// browser used to snap the view, which looked like a jump. Now, before the page is painted, the
// view is put back where it was (with room added under the Record's two columns when the page
// became too short), then it scrolls smoothly so the open step sits just under the sticky header.
import { useCallback, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';

const token = (el: Element, name: string) =>
  parseFloat(getComputedStyle(el).getPropertyValue(name)) || 0;

export function useNextStepScroll() {
  const [asked, setAsked] = useState(0);
  const from = useRef(0);

  useLayoutEffect(() => {
    // The Record's one steps card.
    const box = document.querySelector<HTMLElement>('.card.steps');
    if (asked === 0 || !box) return;
    // The Record's two columns: the room goes under both, so it grows the page whichever is taller.
    const row = box.closest<HTMLElement>('.rgrid') ?? box;
    // The room added after an earlier tick goes first, so the page's real height is measured.
    row.style.marginBottom = '';
    const next = box.querySelector<HTMLElement>('.step.now');
    const head = document.querySelector('.rhead-stick');
    const offset =
      token(box, '--topbar-h') +
      (head?.getBoundingClientRect().height ?? 0) +
      token(box, '--space-16');
    const target = next
      ? Math.max(0, next.getBoundingClientRect().top + window.scrollY - offset)
      : from.current;
    // Room under the Record's two columns when the page is now too short for either the old view
    // or the target.
    const short =
      Math.max(from.current, target) + window.innerHeight - document.documentElement.scrollHeight;
    if (short > 0) row.style.marginBottom = `${Math.ceil(short)}px`;
    window.scrollTo({ top: from.current, behavior: 'auto' });
    if (!next) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: target, behavior: still ? 'auto' : 'smooth' });
  }, [asked]);

  /** Call just before the steps change because the person ticked or undid one. */
  return useCallback(() => {
    from.current = window.scrollY;
    setAsked((n) => n + 1);
  }, []);
}

/** Glide once for the steps that just loaded, if a send asked for it; any other load does not. */
export function takeGlide(flag: MutableRefObject<boolean>, glide: () => void) {
  if (!flag.current) return;
  flag.current = false;
  glide();
}
